import { useRef, useState } from 'react';

// Lightweight HTML5 drag-and-drop reordering hook.
// No external dependency. Returns handler spreads you can apply to each
// row, plus a visual "dragOverId" you can use to highlight the drop zone.
//
// Usage:
//   const drag = useDragReorder(items, (next) => persist(next));
//   items.map(item => (
//     <div key={item.id} {...drag.rowProps(item.id)} ...>
//       <span {...drag.handleProps()}>☰</span> {item.label}
//     </div>
//   ))

export default function useDragReorder(items, onReorder, idKey = 'id') {
  const dragIdRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const reorder = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const from = items.findIndex(it => it[idKey] === fromId);
    const to = items.findIndex(it => it[idKey] === toId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return {
    rowProps: (id) => ({
      draggable: true,
      onDragStart: (e) => {
        dragIdRef.current = id;
        try { e.dataTransfer.effectAllowed = 'move'; } catch { /* ignore */ }
      },
      onDragOver: (e) => {
        e.preventDefault();
        if (dragOverId !== id) setDragOverId(id);
        try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
      },
      onDragLeave: () => {
        if (dragOverId === id) setDragOverId(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        reorder(dragIdRef.current, id);
        dragIdRef.current = null;
        setDragOverId(null);
      },
      onDragEnd: () => {
        dragIdRef.current = null;
        setDragOverId(null);
      },
      style: { cursor: 'grab' },
    }),
    dragOverId,
  };
}
